import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, StatusPill } from "@/components/common";
import { PaginatedTable } from "@/components/PaginatedTable";
import { Button, Checkbox, Field, SectionCard } from "@/components/ui-kit";
import { ExtractionCandidatesTab } from "@/pages/Ontology";
import { OntologyMiniGraph } from "@/components/OntologyMiniGraph";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { fmtDate, shortId } from "@/lib/utils";
import { Play, Network, X, Layers, RotateCw } from "lucide-react";

export interface DagStep {
  id: string;
  processor: string;
  version?: number;
  config?: Record<string, unknown>;
}
export type DagEdge = [string, string] | { from: string; from_port: "out"; to: string; to_port: "in" };
export interface PipelineDag {
  schema_version?: number;
  steps?: DagStep[];
  edges?: DagEdge[];
  ontology_pin?: string | null;
  layout?: Record<string, { x: number; y: number }>;
}
interface Pipeline {
  id: string; name: string; description: string;
  enabled?: boolean;
  dag?: PipelineDag;
  current_revision?: number;
  last_run?: { status: string; started_at: string | null; ontology_version: string | null } | null;
}
interface CatalogDocument {
  id: string; title?: string; natural_key?: string;
}
interface PipelineTemplate {
  key: string; name: string; description: string; available: boolean;
}
interface PipelineRun {
  id: string; pipeline: string; status: string;
  triggered_by?: string;
  started_at?: string | null; finished_at?: string | null;
  summary?: Record<string, unknown>;
}
interface StepRun {
  id: string; run: string; step_id: string; processor_key: string;
  status: string;
  started_at?: string | null; finished_at?: string | null;
  output?: Record<string, unknown>;
  error?: string;
}
interface StreamCheckpoint {
  id: string; provider: string; stream: string; partition: string; status: string;
  source_position: number; acknowledged_position: number; lag: number; dlq_count: number;
}

export function Pipelines() {
  const ws = useApp((s) => s.workspaceId);
  const { canEdit, canAdmin } = useWorkspacePermissions();
  const qc = useQueryClient();

  const [openPipeline, setOpenPipeline] = useState<Pipeline | null>(null);
  const [canvasTarget, setCanvasTarget] = useState<string | "new" | null>(
    () => new URLSearchParams(window.location.search).get("edit"));
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [runTarget, setRunTarget] = useState<Pipeline | null>(null);
  const [documentId, setDocumentId] = useState("");
  const [newPipelineName, setNewPipelineName] = useState("Standard ingest");
  const [managePipeline, setManagePipeline] = useState<Pipeline | null>(null);
  const [manageName, setManageName] = useState("");
  const [cloneName, setCloneName] = useState("");
  const [archiveConfirmed, setArchiveConfirmed] = useState(false);

  const templates = useQuery<{ results: PipelineTemplate[] }>({
    queryKey: ["pipeline-templates", ws],
    queryFn: () => rawFetch(`/pipelines/pipelines/templates/${ws ? `?workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const createTemplate = useMutation({
    mutationFn: (key: string) => rawFetch("/pipelines/pipelines/templates/", {
      method: "POST", body: JSON.stringify({ workspace: ws, key, name: newPipelineName.trim() }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/pipelines/"] });
      qc.invalidateQueries({ queryKey: ["pipelines-probe", ws] });
    },
  });
  const manage = useMutation({
    mutationFn: ({ pipeline, operation }: { pipeline: Pipeline; operation: "rename" | "clone" | "archive" }) => {
      const name = (operation === "clone" ? cloneName : manageName).trim();
      if (operation === "rename") return rawFetch(`/pipelines/pipelines/${pipeline.id}/`, {
        method: "PATCH", body: JSON.stringify({ name }),
      });
      return rawFetch(`/pipelines/pipelines/${pipeline.id}/${operation}/`, {
        method: "POST", body: JSON.stringify(operation === "clone" ? { name } : { confirm: true }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/pipelines/"] });
      qc.invalidateQueries({ queryKey: ["pipelines-probe", ws] });
      setManagePipeline(null);
      setArchiveConfirmed(false);
    },
  });
  const openManage = (pipeline: Pipeline) => {
    setManagePipeline(pipeline);
    setManageName(pipeline.name);
    setCloneName(`${pipeline.name} copy`);
    setArchiveConfirmed(false);
  };

  const documents = useQuery<{ count: number; results: CatalogDocument[] }>({
    queryKey: ["pipeline-catalog-documents", ws],
    queryFn: () => rawFetch(`/pipelines/documents/?page_size=100${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!runTarget,
  });

  const probe = useQuery<{ count: number; results: Pipeline[] }>({
    queryKey: ["pipelines-probe", ws],
    queryFn: () => rawFetch(`/pipelines/pipelines/?page_size=1${ws ? `&workspace=${ws}` : ""}`),
  });

  const trigger = useMutation({
    mutationFn: ({ id, selectedDocumentId }: { id: string; selectedDocumentId?: string }) => rawFetch(`/pipelines/pipelines/${id}/trigger/`, {
      method: "POST", body: JSON.stringify(selectedDocumentId ? { document_id: selectedDocumentId } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/runs/"] });
      setRunTarget(null);
      setDocumentId("");
    },
  });

  const recover = useMutation({
    mutationFn: (id: string) => rawFetch(`/pipelines/runs/${id}/recover/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/runs/"] });
    },
  });
  const checkpoints = useQuery<{ results: StreamCheckpoint[] }>({
    queryKey: ["stream-checkpoints", ws],
    queryFn: () => rawFetch(`/pipelines/checkpoints/?page_size=100${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const checkpointAction = useMutation({
    mutationFn: ({ id, verb }: { id: string; verb: "acknowledge" | "replay" }) =>
      rawFetch(`/pipelines/checkpoints/${id}/${verb}/`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stream-checkpoints", ws] }),
  });

  const empty = !probe.isLoading && (probe.data?.count ?? 0) === 0;

  if (canvasTarget) return <PipelineCanvas workspace={ws ?? ""} target={canvasTarget}
    canEdit={canEdit} onClose={() => {
      setCanvasTarget(null);
      window.history.replaceState(window.history.state, "", "/pipelines");
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/pipelines/"] });
    }} />;

  return (
    <>
      <PageHeader
        title="Pipelines"
        subtitle="Build and run reviewed processing steps. Saved edits create revisions."
        help={
          <>
            <p>Each pipeline is a directed graph of <strong>processors</strong> (parse, chunk, embed, extract, govern, graph-write). Steps run on the <code>ingestion</code> queue, write traces, and emit a summary; a failed step short-circuits the run but keeps its summary for debugging.</p>
            <p>Use a pipeline to process selected content against a pinned <Link to="/ontology" className="text-accent hover:underline">ontology</Link>. A workspace admin can opt a Catalog source into running a reviewed pipeline after each successful ingest. Graph writing requires reviewed candidates and an available graph store.</p>
          </>
        }
        learnMore={{ to: "/howto", hash: "pipelines", label: "How pipelines work" }}
      />

      {empty && (
        <div className="card mb-4 text-sm">
          No pipelines in this workspace yet. Create one in Studio or install an eligible{" "}
          <Link to="/marketplace" className="text-accent underline">Pack</Link>{" "}
          to start with a reviewed design. You can inspect each step before running it.
        </div>
      )}
      {probe.isError && <ErrorBox error={probe.error} />}
      {canEdit && <div className="mb-4"><Button variant="primary" onClick={() => setCanvasTarget("new")}>New blank canvas</Button></div>}

      <details className="card mb-4 text-sm">
        <summary className="cursor-pointer font-medium">Start from a platform template</summary>
        <p className="mt-2 text-muted">Templates are neutral starting points. The Standard ingest template reads an existing Catalog document and stages candidates for review.</p>
        {templates.isError && <ErrorBox error={templates.error} />}
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          {templates.data?.results.map((template) => (
            <div key={template.key} className="border border-border bg-panel p-3">
              <h3 className="font-medium">{template.name}</h3>
              <p className="mt-1 text-xs text-muted">{template.description}</p>
              {!template.available && <p className="mt-2 text-xs text-muted">No pipeline needed or available yet.</p>}
            </div>
          ))}
        </div>
        {templates.data?.results.some((template) => template.key === "standard-ingest" && template.available) && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="text-xs" htmlFor="new-pipeline-name">Pipeline name</label>
            <input id="new-pipeline-name" className="input min-w-56" value={newPipelineName}
              maxLength={200} onChange={(event) => setNewPipelineName(event.target.value)} />
            <button className="btn-primary" disabled={!canEdit || !newPipelineName.trim() || createTemplate.isPending}
              onClick={() => createTemplate.mutate("standard-ingest")}>Create pipeline</button>
          </div>
        )}
        {createTemplate.isError && <ErrorBox error={createTemplate.error} />}
      </details>

      <h2 className="mb-2 text-sm font-medium text-muted">Definitions</h2>
      <PaginatedTable<Pipeline>
        endpoint="/pipelines/pipelines/"
        baseParams={{ workspace: ws ?? undefined }}
        searchPlaceholder="Search by name or description…"
        cols={[
          { header: "Name", cell: (p) => (
              <button className="text-left text-fg hover:text-accent"
                      onClick={() => setOpenPipeline(p)}
                      title="View DAG">
                {p.name}
              </button>
          ) },
          { header: "Ontology", cell: (p) => <span className="text-muted">{p.last_run?.ontology_version || "Not pinned yet"}</span> },
          { header: "Steps", cell: (p) => (
              <span className="text-muted">{p.dag?.steps?.length ?? 0}</span>
          ) },
          { header: "Last run", cell: (p) => p.last_run ? <span className="flex items-center gap-2"><StatusPill status={p.last_run.status} />{fmtDate(p.last_run.started_at)}</span> : <span className="text-muted">Not run</span> },
          { header: "Trigger", cell: () => <span className="text-muted">Manual</span> },
          { header: "Status", cell: (p) => <span className="text-muted">{p.enabled === false ? "Archived" : "Active"}</span> },
          { header: "", width: "1%", cell: (p) => (
              <div className="flex gap-1">
                {canEdit && <button className="btn-ghost text-[11px]" onClick={() => openManage(p)}>Manage</button>}
                <button className="btn-ghost text-[11px]" onClick={() => setCanvasTarget(p.id)}>Edit canvas</button>
                <button className="btn-ghost text-[11px]"
                        onClick={() => setOpenPipeline(p)}>
                  <Network size={12} className="-mt-0.5 mr-1 inline" />DAG
                </button>
                <button className="btn-primary" disabled={!canEdit || p.enabled === false || trigger.isPending}
                        onClick={() => p.dag?.steps?.some((step) => step.processor === "catalog.document")
                          ? setRunTarget(p) : trigger.mutate({ id: p.id })}>
                  <Play size={12} className="-mt-0.5 mr-1 inline" />Run
                </button>
              </div>
          )},
        ]}
        empty="No pipelines yet."
      />

      <h2 className="mb-2 mt-6 text-sm font-medium text-muted">Runs</h2>
      <PaginatedTable<PipelineRun>
        endpoint="/pipelines/runs/"
        baseParams={{ workspace: ws ?? undefined }}
        statusOptions={[
          { label: "Pending",   value: "pending"   },
          { label: "Running",   value: "running"   },
          { label: "Succeeded", value: "succeeded" },
          { label: "Failed",    value: "failed"    },
          { label: "Cancelled", value: "cancelled" },
        ]}
        searchPlaceholder="Search by pipeline name, triggered_by, or status…"
        refetchInterval={5000}
        cols={[
          { header: "ID", cell: (r) => (
              <button className="font-mono text-xs text-fg hover:text-accent"
                      onClick={() => setOpenRunId(r.id)}
                      title="View steps">
                {shortId(r.id)}
              </button>
          ) },
          { header: "Pipeline", cell: (r) => <code className="font-mono text-xs">{shortId(r.pipeline)}</code> },
          { header: "Status", cell: (r) => (
              <div className="flex flex-col">
                <StatusPill status={r.status} />
                {r.status === "failed" && r.summary && typeof r.summary.error === "string" && (
                  <span className="mt-0.5 max-w-[260px] truncate text-[10px] text-bad"
                        title={`${(r.summary.failed_step as string) || "?"}: ${r.summary.error}`}>
                    {(r.summary.failed_step as string) || "step"}: {r.summary.error as string}
                  </span>
                )}
              </div>
          ) },
          { header: "Started", cell: (r) => fmtDate(r.started_at) },
          { header: "Finished", cell: (r) => fmtDate(r.finished_at) },
          { header: "", width: "1%", cell: (r) => (
              <div className="flex items-center gap-1">
                {canEdit && (r.status === "pending" || r.status === "failed") && (
                  <button className="btn-ghost text-[11px]"
                          onClick={() => recover.mutate(r.id)}
                          disabled={recover.isPending && recover.variables === r.id}
                          title={r.status === "pending"
                            ? "Re-dispatch this run (broker may have missed it)"
                            : "Reset and re-run from the start"}>
                    <RotateCw size={12} className="-mt-0.5 mr-1 inline" />
                    {r.status === "pending" ? "Recover" : "Retry"}
                  </button>
                )}
                <button className="btn-ghost text-[11px]"
                        onClick={() => setOpenRunId(r.id)}>
                  <Layers size={12} className="-mt-0.5 mr-1 inline" />Steps
                </button>
              </div>
          ) },
        ]}
      />

      {(checkpoints.data?.results?.length ?? 0) > 0 && <><h2 className="mb-2 mt-6 text-sm font-medium text-muted">Stream checkpoints</h2>
      <div className="mb-3 rounded border border-warn/40 bg-warn/5 px-3 py-2 text-xs text-warn">
        Checkpoints provide resumable adapter state and DLQ evidence. They do not guarantee exactly-once writes across external systems.
      </div>
      {(
        <div className="card overflow-x-auto p-0">
          <table className="min-w-full text-sm">
            <thead className="bg-border/30 text-left text-xs uppercase text-muted"><tr>
              <th className="px-3 py-2">Stream</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Offset</th><th className="px-3 py-2">Lag</th><th className="px-3 py-2">DLQ</th><th className="px-3 py-2">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-border">{checkpoints.data!.results.map((checkpoint) => (
              <tr key={checkpoint.id}>
                <td className="px-3 py-2"><code>{checkpoint.provider}:{checkpoint.stream}{checkpoint.partition ? `/${checkpoint.partition}` : ""}</code></td>
                <td className="px-3 py-2"><StatusPill status={checkpoint.status} /></td>
                <td className="px-3 py-2 font-mono text-xs">{checkpoint.acknowledged_position}/{checkpoint.source_position}</td>
                <td className="px-3 py-2 font-mono text-xs">{checkpoint.lag}</td>
                <td className="px-3 py-2 font-mono text-xs">{checkpoint.dlq_count}</td>
                <td className="px-3 py-2"><div className="flex gap-1">
                  <button className="btn-ghost text-xs" onClick={() => checkpointAction.mutate({ id: checkpoint.id, verb: "acknowledge" })}>Ack latest</button>
                  <button className="btn-ghost text-xs" onClick={() => checkpointAction.mutate({ id: checkpoint.id, verb: "replay" })}>Request replay</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}</>}
      {checkpoints.isError && <ErrorBox error={checkpoints.error} />}

      {openPipeline && (
        <Drawer title={`DAG · ${openPipeline.name}`}
                onClose={() => setOpenPipeline(null)}>
          <DagView dag={openPipeline.dag} orientation="vertical" />
          <details className="mt-3 text-[11px] text-muted">
            <summary className="cursor-pointer">Advanced: raw JSON</summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded border border-border bg-bg p-2 font-mono text-[10px]">
              {JSON.stringify(openPipeline.dag, null, 2)}
            </pre>
          </details>
        </Drawer>
      )}

      {runTarget && (
        <Drawer title={`Run · ${runTarget.name}`} onClose={() => setRunTarget(null)}>
          <div className="space-y-3 text-sm">
            <p className="text-muted">Choose one Catalog document you can access. The saved pipeline revision is pinned when the run starts. The latest published ontology is pinned if one exists.</p>
            {documents.isLoading && <p className="text-muted">Loading documents…</p>}
            {documents.isError && <ErrorBox error={documents.error} />}
            {documents.isSuccess && documents.data.results.length === 0 && (
              <p className="text-muted">No accessible searchable documents. Add one in <Link to="/catalog" className="text-accent underline">Catalog</Link>.</p>
            )}
            {documents.isSuccess && documents.data.results.length > 0 && <>
              <label className="block" htmlFor="pipeline-document">Catalog document</label>
              <select id="pipeline-document" className="input w-full" value={documentId} onChange={(event) => setDocumentId(event.target.value)}>
                <option value="">Select a document…</option>
                {documents.data.results.map((document) => (
                  <option key={document.id} value={document.id}>{document.title || document.natural_key || "Untitled document"}</option>
                ))}
              </select>
              {documents.data.count > documents.data.results.length && <p className="text-xs text-muted">Showing the first {documents.data.results.length} documents. Search in Catalog for older documents.</p>}
              <button className="btn-primary" disabled={!canEdit || !documentId || trigger.isPending}
                onClick={() => trigger.mutate({ id: runTarget.id, selectedDocumentId: documentId })}>
                <Play size={14} className="mr-1 inline" />Start run
              </button>
            </>}
            {trigger.isError && <ErrorBox error={trigger.error} />}
          </div>
        </Drawer>
      )}

      {managePipeline && <Drawer title={`Manage · ${managePipeline.name}`} onClose={() => setManagePipeline(null)}>
        <div className="space-y-4 text-sm">
          <label className="block" htmlFor="pipeline-manage-name">Pipeline name</label>
          <input id="pipeline-manage-name" className="input w-full" value={manageName} maxLength={200}
            onChange={(event) => setManageName(event.target.value)} />
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" disabled={!canEdit || !manageName.trim() || manageName === managePipeline.name || manage.isPending}
              onClick={() => manage.mutate({ pipeline: managePipeline, operation: "rename" })}>Save name</button>
          </div>
          <label className="block" htmlFor="pipeline-clone-name">Clone name</label>
          <input id="pipeline-clone-name" className="input w-full" value={cloneName} maxLength={200}
            onChange={(event) => setCloneName(event.target.value)} />
          <button className="btn" disabled={!canEdit || !cloneName.trim() || manage.isPending}
            onClick={() => manage.mutate({ pipeline: managePipeline, operation: "clone" })}>Clone pipeline</button>
          {canAdmin && managePipeline.enabled !== false && <div className="border-t border-border pt-4">
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={archiveConfirmed} onChange={(event) => setArchiveConfirmed(event.target.checked)} />
              I understand archiving stops future runs and keeps run history.
            </label>
            <button className="btn mt-2 text-bad" disabled={!archiveConfirmed || manage.isPending}
              onClick={() => manage.mutate({ pipeline: managePipeline, operation: "archive" })}>Archive pipeline</button>
          </div>}
          {manage.isError && <ErrorBox error={manage.error} />}
        </div>
      </Drawer>}

      {openRunId && (
        <Drawer title={`Run · ${shortId(openRunId)}`}
                onClose={() => setOpenRunId(null)}>
          <RunStepsView runId={openRunId} />
        </Drawer>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

type CanvasPalette = { key: string; label: string; kind: string; config: Record<string, { type: string; min: number; max: number }> };
type CanvasDag = PipelineDag & { schema_version: 2; steps: DagStep[]; edges: DagEdge[]; ontology_pin: string | null; layout: Record<string, { x: number; y: number }> };
const blankCanvas = (): CanvasDag => ({ schema_version: 2, steps: [], edges: [], ontology_pin: null, layout: {} });
const pair = (edge: DagEdge): [string, string] => Array.isArray(edge) ? edge : [edge.from, edge.to];
function toCanvas(dag?: PipelineDag): CanvasDag {
  if (!dag) return blankCanvas();
  return { schema_version: 2, ontology_pin: dag.ontology_pin ?? null,
    steps: (dag.steps ?? []).map((step) => ({ ...step, version: 1, config: step.config ?? {} })),
    edges: (dag.edges ?? []).map((edge) => {
      const [from, to] = pair(edge);
      return { from, from_port: "out", to, to_port: "in" };
    }),
    layout: Object.fromEntries((dag.steps ?? []).map((step, index) => [step.id,
      dag.layout?.[step.id] ?? { x: 110 + (index % 3) * 230, y: 90 + Math.floor(index / 3) * 130 }])),
  };
}
function canvasError(dag: CanvasDag): string | null {
  if (!dag.steps.length) return "Add at least one step.";
  if (dag.steps.length > 16 || dag.edges.length > 32) return "Limit this canvas to 16 steps and 32 connections.";
  const ids = dag.steps.map((step) => step.id);
  if (new Set(ids).size !== ids.length) return "Step names must be unique.";
  const links = dag.edges.map((edge) => pair(edge).join("→"));
  if (new Set(links).size !== links.length) return "Remove the duplicate connection.";
  const outgoing = new Map(ids.map((id) => [id, [] as string[]]));
  const indegree = new Map(ids.map((id) => [id, 0]));
  for (const edge of dag.edges) {
    const [from, to] = pair(edge);
    if (!outgoing.has(from) || !indegree.has(to) || from === to) return "A connection refers to an invalid step.";
    outgoing.get(from)!.push(to);
    indegree.set(to, indegree.get(to)! + 1);
  }
  const ready = ids.filter((id) => !indegree.get(id));
  let visited = 0;
  while (ready.length) {
    const id = ready.shift()!; visited += 1;
    for (const next of outgoing.get(id)!) {
      indegree.set(next, indegree.get(next)! - 1);
      if (!indegree.get(next)) ready.push(next);
    }
  }
  return visited === ids.length ? null : "Connections must not contain a cycle.";
}

function PipelineCanvas({ workspace, target, canEdit, onClose }: {
  workspace: string; target: string; canEdit: boolean; onClose: () => void;
}) {
  const existing = target !== "new";
  const palette = useQuery<{ steps: CanvasPalette[] }>({
    queryKey: ["pipeline-design-palette"],
    queryFn: () => rawFetch("/pipelines/pipelines/design-palette/"),
  });
  const pipeline = useQuery<Pipeline>({
    queryKey: ["pipeline-canvas", target],
    queryFn: () => rawFetch(`/pipelines/pipelines/${target}/`), enabled: existing,
  });
  const versions = useQuery<{ results: { id: string; semver: string; status: string }[] }>({
    queryKey: ["pipeline-canvas-ontologies", workspace],
    queryFn: () => rawFetch(`/ontology/versions/?workspace=${encodeURIComponent(workspace)}&page_size=100`),
    enabled: !!workspace,
  });
  const sampleDocuments = useQuery<{ results: CatalogDocument[] }>({
    queryKey: ["pipeline-canvas-documents", workspace],
    queryFn: () => rawFetch(`/pipelines/documents/?workspace=${encodeURIComponent(workspace)}&page_size=100`),
    enabled: !!workspace,
  });
  const [name, setName] = useState("New pipeline");
  const [dag, setDag] = useState<CanvasDag>(blankCanvas);
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState("");
  const [connectTo, setConnectTo] = useState("");
  const [reason, setReason] = useState("");
  const [zoom, setZoom] = useState(1);
  const [saved, setSaved] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(existing ? target : null);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [preview, setPreview] = useState<{ revision: number; digest: string; dag: CanvasDag; queue: string; writes: string[]; runnable: boolean } | null>(null);
  const hydrated = useRef(false);
  useEffect(() => {
    if (!pipeline.data || hydrated.current) return;
    hydrated.current = true;
    setName(pipeline.data.name);
    setDag(toCanvas(pipeline.data.dag));
    setRevision(pipeline.data.current_revision ?? 0);
    setSavedId(pipeline.data.id);
    setSaved(true);
  }, [pipeline.data]);
  const update = (next: CanvasDag) => { setDag(next); setSaved(false); setPreview(null); sample.reset(); };
  const save = useMutation({
    mutationFn: () => rawFetch<{ pipeline_id: string; revision: number; dag: CanvasDag }>("/pipelines/pipelines/save-design/", {
      method: "POST", body: JSON.stringify({ workspace, name: name.trim(), dag, reason: reason.trim(),
        ...(savedId ? { pipeline_id: savedId, expected_revision: revision } : {}) }),
    }),
    onSuccess: (result) => { setSavedId(result.pipeline_id); setRevision(result.revision);
      setDag(result.dag); setSaved(true); setReason(""); setPreview(null); sample.reset(); },
  });
  const loadPreview = useMutation({
    mutationFn: (id: string) => rawFetch<typeof preview>(`/pipelines/pipelines/${id}/deploy-preview/`),
    onSuccess: (result) => setPreview(result),
  });
  const sample = useMutation<{ revision: number; digest: string; graph_writes: number;
    documents: { document_id: string; steps: { step_id: string; counts: Record<string, number | string> }[] }[] }>({
    mutationFn: () => rawFetch(`/pipelines/pipelines/${savedId}/sample/`, {
      method: "POST", body: JSON.stringify({ expected_revision: revision, document_ids: selectedDocuments }),
    }),
  });
  const active = dag.steps.find((step) => step.id === selected);
  const definition = palette.data?.steps.find((step) => step.key === active?.processor);
  const error = canvasError(dag);
  const add = (processor: string) => {
    let number = 1;
    while (dag.steps.some((step) => step.id === `step_${number}`)) number += 1;
    const id = `step_${number}`;
    update({ ...dag, steps: [...dag.steps, { id, processor, version: 1, config: {} }],
      layout: { ...dag.layout, [id]: { x: 110 + (dag.steps.length % 3) * 230,
        y: 90 + Math.floor(dag.steps.length / 3) * 130 } } });
    setSelected(id);
  };
  return <div className="space-y-4 pb-10" aria-label="Pipeline canvas editor">
    <div className="flex flex-wrap items-center justify-between gap-3"><div>
      <h1 className="text-xl font-semibold">Pipeline canvas</h1>
      <p className="text-sm text-muted">Arrange steps, save a revision, then preview exactly what the worker will run. Catalog documents are selected when starting a run.</p>
    </div><Button onClick={onClose}>Back to pipelines</Button></div>
    {(pipeline.isError || palette.isError) && <ErrorBox error={pipeline.error ?? palette.error} />}
    <div className="grid gap-4 xl:grid-cols-[230px_minmax(0,1fr)_270px]">
      <SectionCard title="Steps" summary="Add a reviewed processor">
        <div className="space-y-2">{palette.data?.steps.map((step) =>
          <Button key={step.key} className="w-full text-left" disabled={!canEdit || dag.steps.length >= 16}
            onClick={() => add(step.key)}>+ {step.label}</Button>)}</div>
      </SectionCard>
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted">Select a step. Drag it, or use arrow keys to move it.</span>
          <div className="flex gap-1"><Button onClick={() => setZoom(Math.max(.75, zoom - .25))} aria-label="Zoom out">−</Button>
            <Button onClick={() => setZoom(1)}>Fit</Button>
            <Button onClick={() => setZoom(Math.min(2, zoom + .25))} aria-label="Zoom in">+</Button></div>
        </div>
        <DagView dag={dag} zoom={zoom} selected={selected} onSelect={setSelected} onMove={canEdit ? (id, x, y) =>
          update({ ...dag, layout: { ...dag.layout, [id]: { x: Math.min(4000, x), y: Math.min(4000, y) } } }) : undefined} />
        <div className="flex flex-wrap items-end gap-2">
          <span className="text-xs text-muted">Connect selected output to</span>
          <select className="input" aria-label="Connect to step" value={connectTo} onChange={(event) => setConnectTo(event.target.value)}>
            <option value="">Choose a step</option>{dag.steps.filter((step) => step.id !== selected).map((step) =>
              <option key={step.id} value={step.id}>{step.id}</option>)}</select>
          <Button disabled={!canEdit || !selected || !connectTo || dag.edges.some((edge) => {
            const [from, to] = pair(edge); return from === selected && to === connectTo;
          })} onClick={() => update({ ...dag, edges: [...dag.edges,
            { from: selected, from_port: "out", to: connectTo, to_port: "in" }] })}>Connect</Button>
        </div>
        <div className="flex flex-wrap gap-1">{dag.edges.map((edge) => {
          const [from, to] = pair(edge);
          return <Button key={`${from}-${to}`} disabled={!canEdit} aria-label={`Remove connection ${from} to ${to}`}
            onClick={() => update({ ...dag, edges: dag.edges.filter((item) => item !== edge) })}>{from} → {to} ×</Button>;
        })}</div>
      </div>
      <div className="space-y-4">
        <SectionCard title="Configuration" summary={active ? active.processor : "Select a step"}>
          {active ? <div className="space-y-3">
            <p className="text-xs text-muted">Stable ID: {active.id} · Processor version 1</p>
            {Object.entries(definition?.config ?? {}).map(([key, spec]) =>
              <Field key={key} label={key.replaceAll("_", " ")} hint={`Number from ${spec.min} to ${spec.max}`}>
                <input className="input mt-1 w-full" type="number" min={spec.min} max={spec.max}
                  step={spec.type === "integer" ? 1 : .01} value={String(active.config?.[key] ?? "")}
                  onChange={(event) => update({ ...dag, steps: dag.steps.map((step) => step.id === selected
                    ? { ...step, config: { ...step.config, [key]: Number(event.target.value) } } : step) })} />
              </Field>)}
            <Button variant="danger" disabled={!canEdit} onClick={() => {
              const { [selected]: _removed, ...layout } = dag.layout; void _removed;
              update({ ...dag, steps: dag.steps.filter((step) => step.id !== selected),
                edges: dag.edges.filter((edge) => !pair(edge).includes(selected)), layout });
              setSelected("");
            }}>Remove step</Button>
          </div> : <p className="text-xs text-muted">Choose a node on the canvas to edit its configuration.</p>}
        </SectionCard>
        <SectionCard title="Validation and preview" summary={error ?? "Ready to save"}>
          <div className="space-y-3">
            <Field label="Pipeline name"><input className="input mt-1 w-full" value={name} maxLength={200}
              onChange={(event) => { setName(event.target.value); setSaved(false); setPreview(null); }} /></Field>
            <Field label="Ontology version" hint="Pin the version used by extraction. A run cannot silently switch it.">
              <select className="input mt-1 w-full" value={dag.ontology_pin ?? ""}
                onChange={(event) => update({ ...dag, ontology_pin: event.target.value || null })}>
                <option value="">No pin</option>{versions.data?.results.map((item) =>
                  <option key={item.id} value={item.id}>{item.semver} · {item.status}</option>)}</select>
            </Field>
            {!dag.ontology_pin && dag.steps.some((step) => step.processor === "extract.entities") &&
              <p className="text-xs text-warn">Extraction needs an ontology. Pin a reviewed version before running.</p>}
            <Field label="Revision reason"><input className="input mt-1 w-full" value={reason} maxLength={500}
              onChange={(event) => setReason(event.target.value)} /></Field>
            <Button variant="primary" disabled={!canEdit || !!error || !name.trim() || save.isPending || saved}
              onClick={() => save.mutate()}>Save revision</Button>
            <Button disabled={!saved || !savedId || loadPreview.isPending}
              onClick={() => savedId && loadPreview.mutate(savedId)}>Preview saved revision</Button>
            {saved && <p role="status" className="text-xs text-good">Saved revision {revision}. Runs pin this revision when started.</p>}
            {save.isError && <ErrorBox error={save.error} />}
            {loadPreview.isError && <ErrorBox error={loadPreview.error} />}
            {preview && <div role="status" className="border border-border p-2 text-xs">
              <p>Revision {preview.revision} · SHA-256 {preview.digest.slice(0, 12)}…</p>
              <p>Queue: {preview.queue}. Writes: {preview.writes.join(", ") || "none"}.</p>
              <p>{preview.runnable ? "Runnable" : "A processor is unavailable"}. Open Run on the Pipelines list to choose a document.</p>
            </div>}
            {preview?.runnable && <div className="border-t border-border pt-3 text-xs">
              <p className="font-medium">Run on sample</p>
              <p className="mt-1 text-muted">Choose up to three documents you can access. This test uses the saved revision and writes no candidates or graph data.</p>
              {sampleDocuments.isError && <ErrorBox error={sampleDocuments.error} />}
              <div className="mt-2 max-h-32 space-y-1 overflow-auto">
                {sampleDocuments.data?.results.map((document) =>
                  <label key={document.id} className="flex items-center gap-2">
                    <Checkbox checked={selectedDocuments.includes(document.id)}
                      disabled={!selectedDocuments.includes(document.id) && selectedDocuments.length >= 3}
                      onChange={(event) => setSelectedDocuments(event.target.checked
                        ? [...selectedDocuments, document.id]
                        : selectedDocuments.filter((id) => id !== document.id))} />
                    {document.title || document.natural_key || "Untitled document"}
                  </label>)}
                {sampleDocuments.data?.results.length === 0 && <p className="text-muted">No accessible Catalog documents yet.</p>}
              </div>
              <Button className="mt-2" disabled={!canEdit || !saved || !selectedDocuments.length || sample.isPending}
                onClick={() => sample.mutate()}>Test saved revision</Button>
              {sample.isError && <ErrorBox error={sample.error} />}
              {sample.data && <div role="status" className="mt-2 border border-border p-2">
                <p>{sample.data.documents.length} document{sample.data.documents.length === 1 ? "" : "s"} tested · {sample.data.graph_writes} graph writes.</p>
                {sample.data.documents.map((document) => <div key={document.document_id} className="mt-2">
                  <p className="font-medium">{sampleDocuments.data?.results.find((item) => item.id === document.document_id)?.title || "Catalog document"}</p>
                  {document.steps.map((step) => <p key={step.step_id} className="text-muted">{step.step_id}: {Object.entries(step.counts)
                    .map(([key, value]) => `${key.replaceAll("_", " ")} ${value}`).join(", ") || "completed"}</p>)}
                </div>)}
              </div>}
            </div>}
          </div>
        </SectionCard>
      </div>
    </div>
  </div>;
}

function Drawer({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
<div className="fixed inset-0 z-40 flex justify-end">
  <div
    className="flex-1 bg-black/20 dark:bg-black/40 backdrop-blur-[1px]"
    onClick={onClose}
  />

  <div
    className="
      h-full
      w-full
      max-w-2xl
      overflow-y-auto
      bg-card/95
      backdrop-blur-md
      border-l border-border
      p-4
      shadow-2xl
    "
    onClick={(e) => e.stopPropagation()}
  >
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-base font-medium">{title}</h3>

      <button
        type="button"
        className="btn-ghost"
        onClick={onClose}
        aria-label="Close"
      >
        <X size={14} />
      </button>
    </div>

    {children}
  </div>
</div>
  );
}

export function DagView({ dag, orientation = "horizontal", onSelect, onMove, selected, zoom = 1 }: {
  dag: PipelineDag | undefined; orientation?: "horizontal" | "vertical";
  onSelect?: (id: string) => void; onMove?: (id: string, x: number, y: number) => void;
  selected?: string; zoom?: number;
}) {
  const drag = useRef<{ id: string; x: number; y: number; px: number; py: number } | null>(null);
  const steps = dag?.steps ?? [];
  const edges: [string, string][] = (dag?.edges ?? []).map((edge) =>
    Array.isArray(edge) ? edge : [edge.from, edge.to]);

  if (steps.length === 0) {
    return (
      <div className="card text-center text-xs text-muted">
        This pipeline has no steps. Author the DAG via
        <code className="mx-1">POST /pipelines/pipelines/</code>
        or install a Pack.
      </div>
    );
  }

  const rank = new Map<string, number>();
  const incoming = new Map<string, Set<string>>();
  steps.forEach((s) => incoming.set(s.id, new Set()));
  edges.forEach(([f, t]) => {
    if (incoming.has(t)) incoming.get(t)!.add(f);
  });

  let frontier = steps.filter((s) => (incoming.get(s.id)?.size ?? 0) === 0);
  if (frontier.length === 0) frontier = [steps[0]];
  let level = 0;
  const placed = new Set<string>();
  while (frontier.length && placed.size < steps.length) {
    frontier.forEach((s) => { rank.set(s.id, level); placed.add(s.id); });
    const next = steps.filter((s) =>
      !placed.has(s.id) &&
      [...(incoming.get(s.id) ?? [])].every((p) => placed.has(p))
    );
    if (next.length === 0) {
      const leftovers = steps.filter((s) => !placed.has(s.id));
      leftovers.forEach((s) => { rank.set(s.id, level + 1); placed.add(s.id); });
      break;
    }
    frontier = next;
    level += 1;
  }

  const ranks: DagStep[][] = [];
  steps.forEach((s) => {
    const r = rank.get(s.id) ?? 0;
    if (!ranks[r]) ranks[r] = [];
    ranks[r].push(s);
  });

  const colW = 210;
  const rowH = 84;
  const padX = 28;
  const padY = 28;
  const NW = 158;   // node width
  const NH = 56;    // node height
  const vertical = orientation === "vertical";
  const W = padX * 2 + (vertical ? Math.max(1, ...ranks.map((r) => r.length)) : Math.max(1, ranks.length)) * colW;
  const H = padY * 2 + (vertical ? Math.max(1, ranks.length) : Math.max(1, ...ranks.map((r) => r.length))) * rowH;

  const pos = new Map<string, { x: number; y: number }>();
  ranks.forEach((col, ci) => {
    col.forEach((s, ri) => {
      pos.set(s.id, {
        x: padX + (vertical ? ri : ci) * colW + colW / 2,
        y: padY + (vertical ? ci : ri) * rowH + rowH / 2,
      });
    });
  });
  if (dag?.layout) steps.forEach((step) => {
    const point = dag.layout?.[step.id];
    if (point) pos.set(step.id, point);
  });
  const canvasW = dag?.layout ? Math.max(W, ...[...pos.values()].map((p) => p.x + NW)) : W;
  const canvasH = dag?.layout ? Math.max(H, ...[...pos.values()].map((p) => p.y + NH)) : H;
  const order = new Map<string, number>();
  steps.forEach((s, i) => order.set(s.id, i));

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-4 rounded-sm bg-gradient-to-r from-accent/80 to-accent/30" />
          {steps.length} step{steps.length === 1 ? "" : "s"}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-px w-5 bg-accent/60" />
          {edges.length} edge{edges.length === 1 ? "" : "s"}
        </span>
        <span className="ml-auto border border-border bg-bg/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted/80">
          {onMove ? "editable canvas" : "read-only diagram"}
        </span>
      </div>
      <div className="overflow-auto border border-border bg-gradient-to-b from-panel to-bg p-3">
        <svg viewBox={`0 0 ${canvasW} ${canvasH}`} width={`${zoom * 100}%`} style={{ height: vertical ? "min(70vh, 720px)" : undefined }} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`Pipeline diagram with ${steps.length} steps`}>
          <defs>
            <pattern id="dag-grid" width="22" height="22" patternUnits="userSpaceOnUse">
              <path d="M22 0H0V22" fill="none" stroke="hsl(var(--accent) / 0.06)" strokeWidth="1" />
            </pattern>
            <linearGradient id="dag-node" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--panel))" />
              <stop offset="100%" stopColor="hsl(var(--bg))" />
            </linearGradient>
            <linearGradient id="dag-edge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="hsl(var(--accent) / 0.25)" />
              <stop offset="100%" stopColor="hsl(var(--accent) / 0.85)" />
            </linearGradient>
            <marker id="dag-arrow" markerWidth="10" markerHeight="10"
                    refX="8" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill="hsl(var(--accent))" />
            </marker>
            <filter id="dag-shadow" x="-20%" y="-20%" width="140%" height="160%">
              <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity="0.35" />
            </filter>
          </defs>

          <rect x="0" y="0" width={canvasW} height={canvasH} fill="url(#dag-grid)" />

          {edges.map(([from, to], i) => {
            const a = pos.get(from); const b = pos.get(to);
            if (!a || !b) return null;
            const x1 = a.x + NW / 2, x2 = b.x - NW / 2;
            const mx = (x1 + x2) / 2;
            const d = vertical
              ? `M ${a.x} ${a.y + NH / 2} C ${a.x} ${(a.y + b.y) / 2}, ${b.x} ${(a.y + b.y) / 2}, ${b.x} ${b.y - NH / 2}`
              : `M ${x1} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${x2} ${b.y}`;
            return (
              <path key={i} d={d} fill="none" stroke="url(#dag-edge)"
                    strokeWidth="1.75" markerEnd="url(#dag-arrow)" />
            );
          })}

          {steps.map((s) => {
            const p = pos.get(s.id)!;
            const idx = (order.get(s.id) ?? 0) + 1;
            return (
              <g key={s.id} transform={`translate(${p.x - NW / 2}, ${p.y - NH / 2})`}
                role={onSelect ? "button" : undefined} tabIndex={onSelect ? 0 : undefined}
                aria-label={`${s.id}: ${s.processor}`}
                onClick={() => onSelect?.(s.id)}
                onKeyDown={(event) => {
                  if (!onMove) return;
                  const delta = { ArrowLeft: [-20, 0], ArrowRight: [20, 0], ArrowUp: [0, -20], ArrowDown: [0, 20] }[event.key];
                  if (delta) { event.preventDefault(); onSelect?.(s.id); onMove(s.id, Math.max(0, p.x + delta[0]), Math.max(0, p.y + delta[1])); }
                }}
                onPointerDown={(event) => {
                  if (!onMove) return;
                  onSelect?.(s.id);
                  drag.current = { id: s.id, x: p.x, y: p.y, px: event.clientX, py: event.clientY };
                  event.currentTarget.setPointerCapture(event.pointerId);
                }}
                onPointerUp={(event) => {
                  const start = drag.current;
                  drag.current = null;
                  if (!start || !onMove) return;
                  const svg = event.currentTarget.ownerSVGElement;
                  const rect = svg?.getBoundingClientRect();
                  if (!rect) return;
                  onMove(start.id, Math.max(0, Math.round(start.x + (event.clientX - start.px) * canvasW / rect.width)),
                    Math.max(0, Math.round(start.y + (event.clientY - start.py) * canvasH / rect.height)));
                }}>
                <title>{`${s.id}: ${s.processor}`}</title>
                <rect width={NW} height={NH} rx="3" fill="url(#dag-node)"
                      stroke={selected === s.id ? "hsl(var(--accent))" : "hsl(var(--accent) / 0.35)"}
                      strokeWidth={selected === s.id ? "2" : "1"}
                      filter="url(#dag-shadow)" />
                {/* accent leading edge — gives the node a directional, app-grade feel */}
                <rect width="4" height={NH} rx="2" fill="hsl(var(--accent))" />
                <rect x="-1" width="6" height={NH} fill="url(#dag-node)" opacity="0" />
                {/* step index chip */}
                <circle cx="22" cy={NH / 2} r="11" fill="hsl(var(--accent) / 0.12)"
                        stroke="hsl(var(--accent) / 0.5)" strokeWidth="1" />
                <text x="22" y={NH / 2 + 3.5} textAnchor="middle"
                      fontSize="10" fontWeight="700" fill="hsl(var(--accent))">
                  {idx}
                </text>
                <text x="40" y={NH / 2 - 4} fontSize="11.5" fontWeight="600"
                      fill="hsl(var(--text))">
                  {s.id.length > 16 ? s.id.slice(0, 15) + "…" : s.id}
                </text>
                <text x="40" y={NH / 2 + 11} fontSize="9.5" fill="hsl(var(--text))">
                  {s.processor.length > 18 ? s.processor.slice(0, 17) + "…" : s.processor}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function RunStepsView({ runId }: { runId: string }) {
  const qc = useQueryClient();
  const ws = useApp((state) => state.workspaceId);
  const { canAdmin, canEdit } = useWorkspacePermissions();
  const runQuery = useQuery<PipelineRun>({
    queryKey: ["pipeline-run", runId],
    queryFn: () => rawFetch(`/pipelines/runs/${runId}/`),
    refetchInterval: (query) => ["pending", "running"].includes(query.state.data?.status ?? "") ? 2000 : false,
  });
  const cancel = useMutation({
    mutationFn: () => rawFetch(`/pipelines/runs/${runId}/cancel/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pipeline-run", runId] });
      qc.invalidateQueries({ queryKey: ["pipeline-steps", runId] });
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/runs/"] });
    },
  });
  const promote = useMutation<{ candidates_reviewed: number; nodes_written: number; edges_written: number }>({
    mutationFn: () => rawFetch(`/pipelines/runs/${runId}/promote-approved/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["semantic-candidates", ws] });
      qc.invalidateQueries({ queryKey: ["pipeline-run", runId] });
      qc.invalidateQueries({ queryKey: ["pipeline-graph", ws] });
    },
  });
  const promotion = runQuery.data?.summary?.graph_promotion as {
    candidates_reviewed: number; nodes_written: number; edges_written: number;
  } | undefined;
  const graph = useQuery<{
    nodes: { id: string; labels?: string[]; properties?: Record<string, unknown> }[];
    edges: { id: string; type: string; src: string; dst: string }[];
    node_count: number; edge_count: number;
  }>({
    queryKey: ["pipeline-graph", ws],
    queryFn: () => rawFetch(`/graph/instances/${ws}/?limit=30`),
    enabled: canAdmin && !!ws && !!promotion,
  });
  const reviewedTypes = useQuery<{ results: {
    status: string; kind: string; entity_type: string; relation_type: string;
  }[] }>({
    queryKey: ["pipeline-reviewed-types", runId],
    queryFn: () => rawFetch(`/semantic/candidates/?workspace=${ws}&pipeline_run=${runId}&page_size=200`),
    enabled: canAdmin && !!promotion && !!ws,
  });
  const q = useQuery<{ count: number; results: StepRun[] }>({
    queryKey: ["pipeline-steps", runId],
    queryFn: () => rawFetch(
      `/pipelines/steps/?run=${encodeURIComponent(runId)}&page_size=200&ordering=created_at`),
    refetchInterval: () => ["pending", "running"].includes(runQuery.data?.status ?? "") ? 3000 : false,
  });

  const recover = useMutation({
    mutationFn: () => rawFetch(`/pipelines/runs/${runId}/recover/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pipeline-steps", runId] });
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/runs/"] });
    },
  });

  if (q.isLoading) return <div className="text-xs text-muted">Loading steps…</div>;
  if (q.isError) return <ErrorBox error={q.error} />;
  const rows = q.data?.results ?? [];
  if (rows.length === 0) {
    return (
      <div className="card space-y-3 text-center text-xs text-muted">
        <div>{runQuery.data?.status === "cancelled"
          ? "Run cancelled before the worker started."
          : "No steps recorded yet. The worker has not picked up this run."}</div>
        {runQuery.data?.status === "pending" && <div className="text-[11px]">
          This usually means the Celery worker for the
          {" "}<code>ingestion</code>{" "}queue is offline, or the original
          {" "}<code>task.delay</code>{" "}was lost. Use <strong>Recover</strong>
          to re-dispatch; if the broker is down it will fall back to
          inline execution so the run still completes.
        </div>}
        {canEdit && runQuery.data?.status === "pending" && <div className="flex justify-center gap-2">
          <button className="btn-primary inline-flex items-center gap-1 text-xs"
                  onClick={() => recover.mutate()}
                  disabled={recover.isPending}>
            <RotateCw size={12} />
            {recover.isPending ? "Re-dispatching…" : "Recover run"}
          </button>
          <Button variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate()}>Cancel run</Button>
        </div>}
        {recover.isError && <ErrorBox error={recover.error} />}
        {cancel.isError && <ErrorBox error={cancel.error} />}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 border border-border bg-panel p-3 text-xs">
        <div><span className="font-medium">Run status: </span><StatusPill status={runQuery.data?.status ?? "pending"} />
          <span className="ml-2 text-muted">{rows.length} step{rows.length === 1 ? "" : "s"} recorded</span></div>
        {canEdit && ["pending", "running"].includes(runQuery.data?.status ?? "") &&
          <Button variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate()}>Cancel run</Button>}
        {cancel.isError && <ErrorBox error={cancel.error} />}
      </div>
      {canAdmin && runQuery.data?.status === "succeeded" && <div className="border border-border bg-panel p-3 text-xs">
        <p className="mb-2 text-muted">Review candidates below. Promotion writes only approved items to the configured graph and keeps the source document access rules.</p>
        <button className="btn-primary" disabled={promote.isPending} onClick={() => promote.mutate()}>Promote approved to graph</button>
        {promote.isSuccess && <p role="status" className="mt-2 text-good">{promote.data.nodes_written} nodes and {promote.data.edges_written} edges written from {promote.data.candidates_reviewed} approved candidates.</p>}
        {promote.isError && <ErrorBox error={promote.error} />}
      </div>}
      {canAdmin && promotion && <SectionCard title="Graph result" summary="Reviewed candidates written to the workspace graph">
        <p className="text-sm text-text">{promotion.nodes_written} entities, {promotion.edges_written} edges from {promotion.candidates_reviewed} approved candidates in this run.</p>
        {reviewedTypes.data && (() => {
          const counts = new Map<string, number>();
          for (const item of reviewedTypes.data.results.filter((row) => row.status === "approved")) {
            const label = item.kind === "relation" ? item.relation_type : item.entity_type;
            if (label) counts.set(label, (counts.get(label) ?? 0) + 1);
          }
          const top = [...counts].sort((a, b) => b[1] - a[1]).slice(0, 5);
          return top.length > 0 && <p className="mt-2 text-xs text-muted">Top reviewed types in this visible sample: {top.map(([label, count]) => `${label} ${count}`).join(" · ")}</p>;
        })()}
        {graph.data && <div className="mt-3">
          <p className="mb-2 text-xs text-muted">Workspace graph sample · {graph.data.node_count} entities · {graph.data.edge_count} edges in the sample</p>
          <OntologyMiniGraph height={220} maxNodes={30} graph={{
            nodes: graph.data.nodes.map((node) => ({ id: node.id,
              label: String(node.properties?.name ?? node.properties?.label ?? node.labels?.[0] ?? "Entity") })),
            edges: graph.data.edges.map((edge) => ({ id: edge.id, label: edge.type,
              source: edge.src, target: edge.dst })),
          }} />
        </div>}
        {graph.isError && <p className="mt-2 text-xs text-warn">Graph preview is unavailable. Check the graph connection; the recorded run counts remain available.</p>}
      </SectionCard>}
      {rows.map((s) => (
        <div key={s.id} className="rounded border border-border p-2 text-xs">
          <div className="flex items-center gap-2">
            <StatusPill status={s.status} />
            <span className="font-medium">{s.step_id}</span>
            <code className="text-[10px] text-muted">{s.processor_key}</code>
            <span className="ml-auto text-[10px] text-muted">
              {fmtDate(s.started_at)} → {fmtDate(s.finished_at)}
            </span>
          </div>
          {s.started_at && s.finished_at && <p className="mt-1 text-muted">Duration: {Math.max(0,
            Math.round((new Date(s.finished_at).getTime() - new Date(s.started_at).getTime()) / 1000))}s</p>}
          {s.error && (
            <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-bad/5 p-1 text-[10px] text-bad">
              {s.error}
            </pre>
          )}
          {s.output && Object.keys(s.output).length > 0 && <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-muted">
            {Object.entries(s.output).filter(([key, value]) => key !== "document_id" && (typeof value === "number" || typeof value === "string"))
              .map(([key, value]) => <span key={key}>{key.replaceAll("_", " ")}: {String(value)}</span>)}
          </div>}
        </div>
      ))}
      {canAdmin && <ExtractionCandidatesTab ws={ws} runId={runId} />}
    </div>
  );
}
